"use client";

import {
  BadgeCheck, CalendarDays, Camera, Clock, File, FileCheck2,
  FileSearch, FileText, Image, Mic, Scale, Shield, type LucideIcon,
} from "lucide-react";
import { useDataContext } from "@/app/_context";
import type {
  PublicChapter,
  PublicEvidenceDocument,
  PublicInvestigation,
  PublicResearchNote,
} from "@/lib/public-investigation";
import { toPlayerClock } from "@/lib/recording-clock";
import { formatTimecode } from "@/lib/timecode";
import { cn } from "@/lib/utils";
import type { PlayAtTarget } from "./usePlayAt";

// The research notes, chapters and evidence documents a journalist synced from
// the Stone desktop app, shown under the project's findings. All of it is
// author-written text: rendered as text, never parsed as HTML.

const CATEGORY_LABELS: Record<string, string> = {
  foi_timeline: "FOI timeline",
  raw_evidence: "Raw evidence",
  bts_vlog: "Behind the scenes",
  interview: "Interview",
  legal_brief: "Legal brief",
  contract_audit: "Contract audit",
  fact_check: "Fact check",
};
const IMPORTANCE_LABELS: Record<string, string> = {
  crucial: "Crucial",
  supporting: "Supporting",
  background: "Background",
};
const CLASSIFICATION_LABELS: Record<string, string> = {
  FOI: "FOI",
  GIPA: "GIPA",
  LEAKED: "Leaked",
  PUBLIC_RECORD: "Public record",
  CONFIDENTIAL_SOURCE: "Confidential source",
  COURT_DOCUMENT: "Court document",
  CONTRACT: "Contract",
};
const FILE_TYPE_LABELS: Record<string, string> = {
  pdf: "PDF",
  image: "Image",
  contract: "Contract",
  foi_release: "FOI release",
  court_doc: "Court document",
  other: "Other",
};
const TIER_LABELS: Record<string, string> = {
  subscribers_only: "Subscribers only",
  restricted_sleuth: "Restricted",
};

const TYPE_ICONS: Record<string, LucideIcon> = {
  foi_timeline: CalendarDays,
  raw_evidence: FileSearch,
  bts_vlog: Camera,
  interview: Mic,
  legal_brief: Scale,
  contract_audit: FileCheck2,
  fact_check: BadgeCheck,
  pdf: FileText,
  image: Image,
  contract: FileCheck2,
  foi_release: FileSearch,
  court_doc: Scale,
  FOI: FileSearch,
  GIPA: FileSearch,
  LEAKED: Shield,
  PUBLIC_RECORD: FileText,
  CONFIDENTIAL_SOURCE: Shield,
  COURT_DOCUMENT: Scale,
  CONTRACT: FileCheck2,
};

function TypeBadge({ value, labels }: {
  value: string;
  labels: Record<string, string>;
}) {
  if (!value) return null;
  const Icon = TYPE_ICONS[value] ?? File;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-accent/20 bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent">
      <Icon size={12} aria-hidden="true" className="shrink-0" />
      {label(labels, value)}
    </span>
  );
}

// A value a newer desktop app adds still reads as words.
function label(labels: Record<string, string>, value: string) {
  if (!value) return "";
  if (labels[value]) return labels[value];
  const words = value.replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Fixed locale and time zone, so the server and the browser agree.
const DATE_FORMAT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

function formatDate(value: string) {
  if (!value) return "";
  const time = Date.parse(
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value
  );
  return Number.isFinite(time) ? DATE_FORMAT.format(time) : value;
}

const TIMESTAMP_FORMAT = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "UTC",
  timeZoneName: "short",
});

function formatTimestamp(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  const time = Date.parse(value);
  return Number.isFinite(time) ? TIMESTAMP_FORMAT.format(time) : value;
}

function joinParts(parts: string[]) {
  return parts.filter(Boolean).join(" · ");
}

// Which session a moment or link points to, and where it plays.
function useMomentPlayer(playAt: (target: PlayAtTarget) => void) {
  const {
    currentPlaylist,
    currentSessionIndex,
    setCurrentSessionIndex,
    isHighlightsOnlyMode,
  } = useDataContext();

  const findSession = (sessionId: string) =>
    currentPlaylist.findIndex((item) => item.id.toLowerCase() === sessionId);

  // Where a moment plays: null when its session isn't in this project, or
  // when the highlights-only player can't reach it.
  const resolve = (sessionId: string, atMs: number): PlayAtTarget | null => {
    const index = findSession(sessionId);
    if (index === -1) return null;
    const item = currentPlaylist[index];
    if (isHighlightsOnlyMode && !item.highlightsOnlyStream) return null;
    const playerMs = toPlayerClock(atMs, item.segments, isHighlightsOnlyMode);
    return playerMs === null ? null : { index, playerMs };
  };

  return {
    playlist: currentPlaylist,
    currentSessionIndex,
    findSession,
    resolve,
    play: playAt,
    openSession: (index: number) => setCurrentSessionIndex(index),
  };
}

type MomentPlayer = ReturnType<typeof useMomentPlayer>;

function Moments({
  note,
  player,
}: {
  note: PublicResearchNote;
  player: MomentPlayer;
}) {
  const moments = note.moments.filter(
    (moment) => player.findSession(moment.sessionId) !== -1
  );
  if (!moments.length) return null;

  return (
    <ul className="mt-2 flex flex-col gap-1">
      {moments.map((moment) => {
        const target = player.resolve(moment.sessionId, moment.atMs);
        const index = player.findSession(moment.sessionId);
        const elsewhere =
          index !== player.currentSessionIndex
            ? player.playlist[index]?.title
            : "";
        return (
          <li key={`${moment.sessionId}-${moment.atMs}`}>
            <button
              type="button"
              disabled={!target}
              title={
                target
                  ? `Play from ${formatTimecode(moment.atMs)}`
                  : "Not in the highlights. Switch to the full recording to watch it."
              }
              onClick={() => target && player.play(target)}
              className={cn(
                "flex w-full items-start gap-1.5 rounded-md px-1.5 py-1 text-left text-xs",
                target ? "hover:bg-purple-500/5" : "cursor-default opacity-50"
              )}
            >
              <Clock
                size={12}
                className="mt-0.5 shrink-0 text-accent"
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="font-medium text-accent">
                  {formatTimecode(moment.atMs)}
                </span>
                {moment.title ? ` ${moment.title}` : ""}
                {elsewhere ? (
                  <span className="block truncate text-muted-foreground">
                    {elsewhere}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function SessionLinks({
  sessionIds,
  player,
}: {
  sessionIds: string[];
  player: MomentPlayer;
}) {
  const indexes = sessionIds
    .map(player.findSession)
    .filter((index, i, all) => index !== -1 && all.indexOf(index) === i);
  if (!indexes.length) return null;

  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Sessions:{" "}
      {indexes.map((index, i) => (
        <span key={index}>
          {i > 0 && ", "}
          <button
            type="button"
            onClick={() => player.openSession(index)}
            className="text-left text-accent underline-offset-2 hover:underline"
          >
            {player.playlist[index]?.title}
          </button>
        </span>
      ))}
    </p>
  );
}

function DocumentLinks({
  ids,
  documents,
}: {
  ids: string[];
  documents: Map<string, PublicEvidenceDocument>;
}) {
  const linked = ids
    .map((id) => documents.get(id))
    .filter((doc): doc is PublicEvidenceDocument => Boolean(doc));
  if (!linked.length) return null;

  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Documents:{" "}
      {linked
        .map((doc) =>
          doc.sourceClassification
            ? `${doc.title} (${label(
                CLASSIFICATION_LABELS,
                doc.sourceClassification
              )})`
            : doc.title
        )
        .join(", ")}
    </p>
  );
}

const SECTION_HEADING = "mb-2 px-1 text-sm font-semibold";
const CARD = "rounded-md bg-white/10 p-3 dark:bg-zinc-950";

function Chapters({
  chapters,
  documents,
  player,
}: {
  chapters: PublicChapter[];
  documents: Map<string, PublicEvidenceDocument>;
  player: MomentPlayer;
}) {
  return (
    <section aria-labelledby="investigation-chapters">
      <h5 id="investigation-chapters" className={SECTION_HEADING}>
        Chapters
      </h5>
      <ol className="flex flex-col gap-2">
        {chapters.map((chapter) => (
          <li key={chapter.id} className={CARD}>
            <p className="text-xs text-muted-foreground">
              {joinParts([
                chapter.chapterNumber ? `Chapter ${chapter.chapterNumber}` : "",
                chapter.detail ? "" : label(TIER_LABELS, chapter.accessTier),
              ])}
            </p>
            <h6 className="text-sm font-medium">{chapter.title}</h6>
            {chapter.detail?.subtitle ? (
              <p className="text-xs">{chapter.detail.subtitle}</p>
            ) : null}
            {chapter.detail?.summary ? (
              <p className="mt-1 whitespace-pre-line break-words text-xs leading-relaxed">
                {chapter.detail.summary}
              </p>
            ) : null}
            {chapter.detail ? (
              <>
                <SessionLinks
                  sessionIds={chapter.detail.linkedSessionIds}
                  player={player}
                />
                <DocumentLinks
                  ids={chapter.detail.linkedDocumentIds}
                  documents={documents}
                />
              </>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function ResearchNotes({
  notes,
  documents,
  player,
}: {
  notes: PublicResearchNote[];
  documents: Map<string, PublicEvidenceDocument>;
  player: MomentPlayer;
}) {
  return (
    <section aria-labelledby="investigation-research-notes">
      <h5 id="investigation-research-notes" className={SECTION_HEADING}>
        Research notes
      </h5>
      <ol className="flex flex-col gap-2">
        {notes.map((note) => {
          const momentSessions = new Set(note.moments.map((m) => m.sessionId));
          return (
            <li key={note.id} className={CARD}>
              <div className="flex items-start justify-between gap-3">
                <span className="text-xs text-muted-foreground">
                  {joinParts([
                    formatDate(note.eventDate),
                    label(IMPORTANCE_LABELS, note.importance),
                  ])}
                </span>
                <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                  <TypeBadge value={note.category} labels={CATEGORY_LABELS} />
                  {note.isBts && note.category !== "bts_vlog" ? (
                    <TypeBadge value="bts_vlog" labels={CATEGORY_LABELS} />
                  ) : null}
                </div>
              </div>
              {note.title ? (
                <h6 className="mt-1 text-sm font-medium">{note.title}</h6>
              ) : null}
              {note.content ? (
                <p className="mt-1 whitespace-pre-line break-words text-xs leading-relaxed">
                  {note.content}
                </p>
              ) : null}
              <Moments note={note} player={player} />
              <SessionLinks
                sessionIds={note.linkedSessionIds.filter(
                  (id) => !momentSessions.has(id)
                )}
                player={player}
              />
              <DocumentLinks
                ids={note.linkedDocumentIds}
                documents={documents}
              />
              {note.storyTags.length ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Tags: {note.storyTags.join(", ")}
                </p>
              ) : null}
              {note.custody?.verifiedAt ? (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  <time dateTime={note.custody.verifiedAt} title="Recorded at">
                    {formatTimestamp(note.custody.verifiedAt)}
                  </time>
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Documents({ documents }: { documents: PublicEvidenceDocument[] }) {
  return (
    <section aria-labelledby="investigation-documents">
      <h5 id="investigation-documents" className={SECTION_HEADING}>
        Documents
      </h5>
      <p className="-mt-1 mb-2 px-1 text-xs text-muted-foreground">
        Listed for reference. The files are held by the journalist.
      </p>
      <ul className="flex flex-col gap-2">
        {documents.map((doc) => (
          <li key={doc.id} className={CARD}>
            <div className="flex items-start justify-between gap-3">
              <h6 className="min-w-0 text-sm font-medium">{doc.title}</h6>
              <div className="ml-auto flex flex-wrap justify-end gap-1.5">
                <TypeBadge value={doc.fileType} labels={FILE_TYPE_LABELS} />
                <TypeBadge
                  value={doc.sourceClassification}
                  labels={CLASSIFICATION_LABELS}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {joinParts([
                doc.referenceNumber ? `Ref. ${doc.referenceNumber}` : "",
                doc.dateObtained
                  ? `Obtained ${formatDate(doc.dateObtained)}`
                  : "",
              ])}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The part of the investigation one session carries: the free chapters that
 * list it, the research notes linked to it or to a moment in it, and the
 * documents those cite.
 */
export function sessionResearch(
  investigation: PublicInvestigation | null | undefined,
  sessionId: string | undefined
) {
  const id = sessionId?.toLowerCase();
  if (!investigation || !id) return null;
  const chapters = investigation.chapters.filter((chapter) =>
    chapter.detail?.linkedSessionIds.includes(id)
  );
  const notes = investigation.researchNotes.filter(
    (note) =>
      note.linkedSessionIds.includes(id) ||
      note.moments.some((moment) => moment.sessionId === id)
  );
  const cited = new Set([
    ...chapters.flatMap((chapter) => chapter.detail?.linkedDocumentIds ?? []),
    ...notes.flatMap((note) => note.linkedDocumentIds),
  ]);
  const documents = investigation.documents.filter((doc) => cited.has(doc.id));
  if (!chapters.length && !notes.length && !documents.length) return null;
  return { chapters, notes, documents, all: investigation.documents };
}

/** One session's research, shown under its highlights and notes. */
export function SessionResearch({
  research,
  playAt,
}: {
  research: NonNullable<ReturnType<typeof sessionResearch>>;
  playAt: (target: PlayAtTarget) => void;
}) {
  const player = useMomentPlayer(playAt);
  const documents = new Map(research.all.map((doc) => [doc.id, doc]));

  return (
    <div className="flex flex-col gap-6 px-2 pt-3 pb-6">
      {research.chapters.length > 0 && (
        <Chapters
          chapters={research.chapters}
          documents={documents}
          player={player}
        />
      )}
      {research.notes.length > 0 && (
        <ResearchNotes
          notes={research.notes}
          documents={documents}
          player={player}
        />
      )}
      {research.documents.length > 0 && (
        <Documents documents={research.documents} />
      )}
    </div>
  );
}

export function hasInvestigationSections(
  investigation: PublicInvestigation | null | undefined
) {
  return Boolean(
    investigation &&
      (investigation.chapters.length ||
        investigation.researchNotes.length ||
        investigation.documents.length)
  );
}

export default function InvestigationDetails({
  investigation,
  playAt,
}: {
  investigation: PublicInvestigation;
  /** From usePlayAt in the column, so a pending seek outlives this tab. */
  playAt: (target: PlayAtTarget) => void;
}) {
  const player = useMomentPlayer(playAt);
  const documents = new Map(
    investigation.documents.map((doc) => [doc.id, doc])
  );

  return (
    <div className="flex flex-col gap-6 px-2 pt-3 pb-6">
      {investigation.chapters.length > 0 && (
        <Chapters
          chapters={investigation.chapters}
          documents={documents}
          player={player}
        />
      )}
      {investigation.researchNotes.length > 0 && (
        <ResearchNotes
          notes={investigation.researchNotes}
          documents={documents}
          player={player}
        />
      )}
      {investigation.documents.length > 0 && (
        <Documents documents={investigation.documents} />
      )}
    </div>
  );
}
